import { EpiPage } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Button, Html, Image, LoadingSkeleton, SmartLink, Title } from '@components';
import { Box, Skeleton, styled } from '@mui/material';
import { colors } from '@themes/colors';
import { spacing } from '@themes/theme.helpers';
import { t } from '@translations';
import classnames from 'classnames';

// TYPES
interface PagePreviewProps {
  block?: EpiPage;
  useCard?: boolean;
  isWorking?: boolean;
  contentItemLinkText?: string;
}

// STYLED COMPONENTS
const PageListItemWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${colors.background.white};
  border-radius: 4px;
`;

const PageImageWrapper = styled(Box)`
  height: 330px;
  img {
    border-radius: 4px;
    height: inherit !important;
  }
  .page-list-item-title {
    padding: ${spacing(2)} 0;
    text-transform: none;
    &:after {
      display: none;
    }
  }
`;

const PageContentBox = styled(Box)`
  -webkit-box-pack: justify;
  height: 100%;
  border-radius: 0 0 4px 4px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: auto;

  span,
  p {
    margin-bottom: 0;
    text-align: unset !important;
  }
  .page-list-item-title {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    min-height: 62px;
  }
  .button-link {
    width: fit-content;
  }
`;

const DescriptionWrapper = styled(Box)`
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  height: inherit;
`;

// COMPONENT
export const PageListItem = ({
  block,
  useCard,
  isWorking,
  contentItemLinkText,
}: PagePreviewProps) => {
  const mainClass = classnames('page-list-item', {
    'card hover-shadow': useCard,
  });
  const hasLink = block?.url || block?.previewLink?.href;

  const pictureSources = [
    {
      media: '(max-width:600px)',
      params: { width: 400, quality: 60, format: 'webp' } as const,
    },
    {
      media: '(max-width:960px)',
      params: { width: 500, quality: 70, format: 'webp' } as const,
    },
    {
      params: { width: 600, quality: 85, format: 'webp' } as const,
    },
  ];

  const sizes = '(max-width:600px) 100vw, (max-width:960px) 50vw, 600px';

  return isWorking ? (
    <PageListItemSkeleton useCard={useCard} />
  ) : (
    <PageListItemWrapper className={mainClass}>
      <PageImageWrapper>
        <Image
          {...block?.imageConfig}
          image={block?.previewImage || block?.image}
          height='330'
          width='500'
          scalingMode='fill'
          pictureSources={pictureSources}
          sizes={sizes}
        />
      </PageImageWrapper>
      <PageContentBox>
        <Title variant='h4' className='page-list-item-title' noDot>
          {block?.previewTitle}
        </Title>
        <DescriptionWrapper>
          <Html html={block?.previewText} />
        </DescriptionWrapper>

        {!block?.previewHideReadMoreButton && hasLink && (
          <SmartLink
            href={block?.url || block?.previewLink.href}
            className='button-link'
            target={block?.previewLink?.target}
          >
            <Button variant='secondary' sx={{ alignSelf: 'flex-start' }}>
              {contentItemLinkText || t(TextCodes.Base.ReadMore)}
            </Button>
          </SmartLink>
        )}
      </PageContentBox>
    </PageListItemWrapper>
  );
};

//create skeleton for page list item
export const PageListItemSkeleton = ({ useCard }: { useCard?: boolean }) => {
  const mainClass = classnames('page-list-item', {
    'card hover-shadow': useCard,
  });
  return (
    <PageListItemWrapper className={mainClass}>
      <PageImageWrapper>
        <Skeleton variant='rectangular' height={330} />
      </PageImageWrapper>
      <PageContentBox>
        <Title variant='h4' className='page-list-item-title' noDot>
          <LoadingSkeleton variant='rectangular' width={'100%'} height={32} />
        </Title>
        <DescriptionWrapper>
          <LoadingSkeleton variant='rectangular' width={'100%'} height={32} />
        </DescriptionWrapper>
        <LoadingSkeleton variant='rectangular' width={100} height={32} />
      </PageContentBox>
    </PageListItemWrapper>
  );
};
